import {
  CommonenumsSortOrder,
  PortfolioManagementcampaignsv1UserAction,
  PortfolioManagementrestrictionsv1SleeveEntity,
  V1AggregationMethod,
  V1RemainingCapacityResponse,
  V1TableType,
} from '@blk/aladdin-graph-client-axios-internal/wealth/portfolio_management/campaigns/v1/campaign_api';
import { PortfolioManagementbenchmarksv1BenchmarkAssignment } from '@blk/aladdin-graph-client-axios-internal/wealth/portfolio_management/portfolios/v1/aggregate_api';
import {
  ResolvedOrderApiListResolvedOrdersData,
  ResolvedOrderApiRefreshOrderAttributesData,
} from '@blk/aladdin-graph-client-axios-internal/wealth/portfolio_management/proposals/v1/resolved_order_api';
import { V1ResolvedOrders } from '@blk/aladdin-graph-client-axios-internal/wealth/portfolio_management/proposals/v1/supplement_api';
import {
  DateAsString,
  ExternalProposalRuleEvaluation,
  ProposalFees,
  ProposalsAggregationStatisticsParams,
} from '@blk/aw-bff-typescript-models';
import { AWSelectedPage } from '@blk/awt-userpreference';
import {
  AGraphMandateApplicationType,
  AGraphRestrictionSeverityType,
  AGraphRestrictionType,
  AGraphRestrictionValueType,
} from '../types';

export type RemainingCapacity = V1RemainingCapacityResponse;

export interface AGraphCampaign {
  campaignType?: string;
  creationTime: string;
  creator: string;
  description?: string;
  endDate?: string;
  exposureReportId?: string;
  id: string;
  lastUpdater: string;
  lastWorkflowLog?: AGraphCampaignLastWorkflowLog;
  metadata?: { [key: string]: MetadataValue };
  name?: string;
  portfolioAnalysisFilter?: {
    accountsFilter: AGraphFilterCriteria;
    positionsFilter: AGraphFilterCriteria;
  };
  portfolioAnalysisReport?: PortfolioAnalysisReport;
  portfolioSelectionAnalytics?: { [key: string]: number };
  portfolioSelectionDate?: string;
  proposalGenerationResults?: { [key: string]: number };
  proposalGenerationUserInput?: AGraphProposalGenerationUserInput;
  proposalPublishParameters?: { [key: string]: string };
  proposalPurgeDate?: string;
  publishDate?: string;
  trackingMetrics?: string[];
  updateTime?: string;
}

// FIXME zhenli : Remove this and its usages from everywhere and replace with APIOperationType.
export enum AGraphFilterCriteriaOperator {
  LOGICAL_OPERATOR_AND = 'LOGICAL_OPERATOR_AND',
  LOGICAL_OPERATOR_OR = 'LOGICAL_OPERATOR_OR',
  LOGICAL_OPERATOR_UNSPECIFIED = 'LOGICAL_OPERATOR_UNSPECIFIED',
}

// AGraph Filters
export interface AGraphFilterCriteria {
  filters: AGraphFilter[];
  operator?: AGraphFilterCriteriaOperator;
}

export interface AGraphFilter {
  fieldDelimiter?: string;
  fieldName: string;
  filterList?: AGraphFilterList;
  filterNullCheck?: AGraphFilterNullCheck;
  filterRange?: AGraphFilterRange;
  filterValue?: AGraphFilterValue;
}

export type AGraphFilterListOperator =
  | 'LIST_COMPARISON_OPERATOR_UNSPECIFIED'
  | 'LIST_COMPARISON_OPERATOR_IN'
  | 'LIST_COMPARISON_OPERATOR_NOT_IN';

export type AGraphFilterValueOperator =
  | 'VALUE_COMPARISON_OPERATOR_UNSPECIFIED'
  | 'VALUE_COMPARISON_OPERATOR_EQUAL'
  | 'VALUE_COMPARISON_OPERATOR_NOT_EQUAL'
  | 'VALUE_COMPARISON_OPERATOR_GREATER_THAN'
  | 'VALUE_COMPARISON_OPERATOR_GREATER_THAN_EQUAL_TO'
  | 'VALUE_COMPARISON_OPERATOR_LESS_THAN'
  | 'VALUE_COMPARISON_OPERATOR_LESS_THAN_EQUAL_TO'
  | 'VALUE_COMPARISON_OPERATOR_STARTS_WITH'
  | 'VALUE_COMPARISON_OPERATOR_ENDS_WITH'
  | 'VALUE_COMPARISON_OPERATOR_CONTAINS';

export type AGraphFilterRangeOperator =
  | 'RANGE_COMPARISON_OPERATOR_UNSPECIFIED'
  | 'RANGE_COMPARISON_OPERATOR_BETWEEN'
  | 'RANGE_COMPARISON_OPERATOR_NOT_BETWEEN';

export type AGraphFilterNullCheckOperator =
  | 'NULL_COMPARISON_OPERATOR_UNSPECIFIED'
  | 'NULL_COMPARISON_OPERATOR_IS_NULL'
  | 'NULL_COMPARISON_OPERATOR_IS_NOT_NULL';

export type ChangeTypeMap =
  | 'CHANGE_TYPE_UNSPECIFIED'
  | 'CHANGE_TYPE_BUY'
  | 'CHANGE_TYPE_SELL'
  | 'CHANGE_TYPE_SELL_ALL'
  | 'CHANGE_TYPE_INJECT'
  | 'CHANGE_TYPE_EJECT';

export interface AGraphFilterList {
  comparisonOperator: AGraphFilterListOperator;
  dateList?: AGraphList<string>;
  doubleList?: AGraphList<number>;
  stringList?: AGraphList<string>;
  timestampList?: AGraphList<string>;
}

export interface AGraphFilterRange {
  comparisonOperator: AGraphFilterRangeOperator;
  dateRange?: AGraphRange<string>;
  doubleRange?: AGraphRange<number>;
  timestampRange?: AGraphRange<string>;
}

export interface AGraphFilterValue {
  comparisonOperator: AGraphFilterValueOperator;
  dateValue?: string;
  doubleValue?: number;
  stringValue?: string;
  timestampvalue?: string;
}

export interface AGraphFilterNullCheck {
  comparisonOperator: AGraphFilterNullCheckOperator;
}

interface AGraphList<T> {
  values: T[];
}

interface AGraphRange<T> {
  end: T;
  start: T;
}

export interface AGraphMandateRestriction {
  application: AGraphMandateApplicationType;
  createTime?: string;
  creator?: string;
  id: string;
  lastUpdater?: string;
  maxValue?: number;
  minValue?: number;
  name: string;
  priority: number;
  restrictionType: AGraphRestrictionType;
  restrictionValueType: AGraphRestrictionValueType;
  scope?: AGraphRestrictionScope;
  severity: AGraphRestrictionSeverityType;
  updateTime?: string;
}

export interface AGraphProposalGenerationUserInput {
  entityOverrides?: EntityOverride[];
  generationParameters?: { [key: string]: MetadataValue };
  reason: string;
  restrictions?: AGraphMandateRestriction[];
}

export interface EntityOverride {
  entityTag: string;
  entityType: string;
  modelReference?: ModelEntityRef;
  restrictions?: AGraphMandateRestriction[];
}

export enum AGraphOrigin {
  ORIGIN_SYSTEM = 'ORIGIN_SYSTEM',
  ORIGIN_USER = 'ORIGIN_USER',
}

export enum AGraphModelTag {
  MODEL_TAG_ENTITLED = 'MODEL_TAG_ENTITLED',
  MODEL_TAG_PUBLIC = 'MODEL_TAG_PUBLIC',
}

export enum AGraphAnalysisIdTypes {
  ACCOUNT_ID = 'ACCOUNT_ID',
  AW_AGGREGATE = 'AW_AGGREGATE',
  CUSTOM_MODEL_ID = 'CUSTOM_MODEL_ID',
  HOUSEHOLD_ID = 'HOUSEHOLD_ID',
  MODEL_ID = 'MODEL_ID',
  PROPOSAL_ID = 'PROPOSAL_ID',
  PROSPECT_ID = 'PROSPECT_ID',
}

export enum AgraphMandateBenchmarkType {
  PRIMARY_BENCHMARK_TYPE_SAA = 'PRIMARY_BENCHMARK_TYPE_SAA',
  PRIMARY_BENCHMARK_TYPE_TAA = 'PRIMARY_BENCHMARK_TYPE_TAA',
  PRIMARY_BENCHMARK_TYPE_UNSPECIFIED = 'PRIMARY_BENCHMARK_TYPE_UNSPECIFIED',
}

export interface ModelEntityRef {
  asOfTime?: DateAsString;
  id: string;
  modelTag: string;
  origin: AGraphOrigin;
}

export interface AGraphRestrictionScope {
  includeAll?: boolean;
  sleeve?: SleeveValues;
  string?: StringValues;
}

export interface AGraphAssetPriceOverrides {
  assetId: string;
  overridePrice: number;
  pricedAsPercentage: false;
}

export enum AGraphCampaignStatus {
  ACTIVE = 'ACTIVE',
  COMPLETED = 'COMPLETED',
  DRAFT = 'DRAFT',
  EXPIRED = 'EXPIRED',
  FAILED_TO_FILTER_PORTFOLIOS = 'FAILED_TO_FILTER_PORTFOLIOS',
  FAILED_TO_PUBLISH = 'FAILED_TO_PUBLISH',
  PORTFOLIO_SEARCH_RUNNING = 'PORTFOLIO_SEARCH_RUNNING',
  PORTFOLIO_SELECTION_COMPLETE = 'PORTFOLIO_SELECTION_COMPLETE',
  PORTFOLIOS_IDENTIFIED = 'PORTFOLIOS_IDENTIFIED',
  PUBLISH_PENDING = 'PUBLISH_PENDING',
}

export interface AGraphCampaignLastWorkflowLog {
  campaignId: string;
  creationTime?: string;
  status: AGraphCampaignStatus;
  user: string;
  userComment?: string;
  uuid?: string;
}

export interface GetCampaignsRequestParams {
  orderBy?: string;
  pageSize?: number;
  pageToken?: string;
}

export interface CampaignsResponse {
  campaigns: AGraphCampaign[];
  nextPageToken: string;
}

export interface MetadataValue {
  doubleValue?: number;
  numberValue?: number;
  stringValue?: string;
}
export interface DynamicIdsForReports {
  displayValue?: string;
  id: string;
  parentAccountId?: string;
  tag?: string;
}

export type OrderedSelectedPage = AWSelectedPage & {
  isSelected: boolean;
  pageDescription?: string;
  pageTitle?: string;
  exhibitOverrides?: Array<{ exhibitKey: string; exhibitTitle?: string; exhibitDescription?: string }>;
  attachments?: string[];
  dynamicSubLevelId?: string;
  pageDataSource?: string;
  overridePropsData?: { [key: string]: string };
};

export type OrderedPageGroups = Array<{
  groupName: string | undefined;
  pages: OrderedSelectedPage[];
  dynamicSubLevelId?: string;
}>;

export type ReportOverride = { templateSetName: string; pageGroups?: OrderedPageGroups };

export type ReportOverrides = ReportOverride[];

export type ReportSettings = {
  settings: {
    encodingVersion: string;
    overrides: ReportOverrides;
  };
};

export interface CampaignReportingConfig {
  portfolioAnalysisDefinition: PortfolioAnalysisDefinition;
  reportSummaryDefinition: ReportSummaryDefinition;
}

export interface ReportSummaryDefinition {
  securityAnalyticsSelectFields?: ReportingSelectFields[];
  selectFields: ReportingSelectFields[];
}

export interface PortfolioAnalysisDefinition {
  responseLimit?: number;
  selectFields: ReportingSelectFields[];
  sortColumns: ReportingSortColumns[];
}

type SortOrder = CommonenumsSortOrder;
type TableType = V1TableType;
export type SelectFieldAggregationMethod = V1AggregationMethod;

export interface ReportingSelectFields {
  aggregationMethod: SelectFieldAggregationMethod;
  displayField?: string;
  fieldName: string;
  tableType: TableType;
  weightedColumn?: string;
}

export interface ReportingSortColumns {
  fieldName: string;
  sortOrder: SortOrder;
}

export interface PutCampaignRequestBody {
  campaign: AGraphCampaign;
  newWorkflowStatus?: {
    campaignStatus: string;
    userComment?: string;
  };
  reportingConfig?: CampaignReportingConfig;
}

export interface UserActionsResponse {
  userActions: Array<PortfolioManagementcampaignsv1UserAction>;
}

export enum AnalysisReportState {
  REPORT_STATE_ERROR = 'REPORT_STATE_ERROR',
  REPORT_STATE_FINISHED = 'REPORT_STATE_FINISHED',
  REPORT_STATE_STALE = 'REPORT_STATE_STALE',
  REPORT_STATE_SUBMITTED = 'REPORT_STATE_SUBMITTED',
  REPORT_STATE_UNSPECIFIED = 'REPORT_STATE_UNSPECIFIED',
}

export interface PortfolioAnalysisReport {
  createTime: string;
  evictTime: string;
  message?: string;
  portfolioAnalyses: PortfolioAnalysis[];
  portfolioAnalysisRequestContext?: PortfolioAnalysisRequestContext;
  portfolioReportEntity: PortfolioReportEntity;
  reportName?: string;
  reportState: AnalysisReportState;
  reportSummary: ReportSummary;
}

export interface PortfolioAnalysisRequestContext {
  request: {
    portfolioReportEntity: PortfolioReportEntity;
    reportName: string;
    entityTag: string;
    viewAsUser: string;
    portfolioAnalysisFilter: RequestPortfolioAnalysisFilter;
    portfolioAnalysisDefinition: RequestPortfolioAnalysisDefinition;
    reportSummaryDefinition: RequestReportSummaryDefinition;
    reportType: string;
  };
}

export interface RequestFilter {
  filters: AGraphFilter[];
  operator: string;
}
export interface RequestPortfolioAnalysisFilter {
  accountsFilter: RequestFilter;
  positionsFilter: RequestFilter;
}

export interface RequestPortfolioAnalysisDefinition {
  responseLimit: number;
  selectFields: RequestSelectField[];
  sortColumns: RequestSortColumn[];
}

export interface RequestSelectField {
  aggregationMethod: string;
  displayField: string;
  fieldName: string;
  tableType: string;
  weightedColumn: string;
}

export interface RequestSortColumn {
  fieldName: string;
  sortOrder: string;
}

export interface RequestReportSummaryDefinition {
  securityAnalyticsSelectFields: RequestSelectField[];
  selectFields: RequestSelectField[];
}

export interface PortfolioReportEntity {
  analysisDate: string;
  pkg: string;
  reportId: string;
  userId: string;
}
export interface ReportSummary {
  securityLevelSummaryStats?: {
    [analyticFieldName: string]: {
      securityContributors: { [assetId: string]: number };
    };
  };
  summaryStats: { [key: string]: number };
}

export interface PortfolioAnalysis {
  analysisValues?: { [key: string]: MetadataValue };
  portfolioId: string;
  portfolioMetadata?: { [key: string]: MetadataValue };
}
export interface ProposalSummaryRequestBody {
  pageSize?: number;
  pageToken?: string;
  pkg: string;
  query?: Query;
}
export interface Query {
  facetPivots?: IFacetPivots;
  facets: QueryFacet[];
  filters?: QueryFilter[];
  sorts?: Sort[];
}

export interface QueryFacet {
  facets: Facet[];
}
export interface QueryFilter {
  filters?: Filter[];
  operator?: string;
}

export interface Facet {
  categoryFacet?: {
    fieldName: string;
    includeMissing?: boolean;
  };
  rangeFacet?: {
    end: number;
    fieldName: string;
    gap: number;
    minCount: number;
    start: number;
  };
}

export interface Filter {
  fieldName?: string;
  negate?: boolean;
  rangeFilter?: RangeFilter;
  valueFilter?: ValueFilter;
}

export interface ValueFilter {
  doubleValues?: DoubleValues;
  stringValues?: StringValues;
}

export interface RangeFilter {
  timestampRange: TimestampRange;
}

export interface TimestampRange {
  end: string | undefined;
  start: string | undefined;
}

export interface SleeveValues {
  values?: PortfolioManagementrestrictionsv1SleeveEntity[];
}

export interface StringValues {
  values?: string[];
}

export interface DoubleValues {
  values?: number[];
}

export interface Sort {
  fieldName: string;
  sortOrder: QuerySortOrder;
}

export enum QuerySortOrder {
  SORT_ORDER_ASCENDING = 'SORT_ORDER_ASCENDING',
  SORT_ORDER_DESCENDING = 'SORT_ORDER_DESCENDING',
  SORT_ORDER_UNSPECIFIED = 'SORT_ORDER_UNSPECIFIED',
}

export interface FieldCount {
  path: string;
  recordValueCounts: { [field: string]: number };
}

export interface ProposalSummaryResponseBody {
  facetPivotResults?: IFacetPivotResults;
  fieldCounts?: FieldCount[];
  nextPageToken?: string;
  proposalSummaries: ProposalSummary[];
  resultsFound: number;
}

export interface ListUserProposalSummariesRequestBody {
  pageSize?: number;
  pageToken?: string;
  pkg: string;
  userOverride?: string;
}

export interface ListUserProposalSummariesResponseBody {
  nextPageToken?: string;
  proposalSummaries: ProposalSummary[];
}

export interface ProposalSummary {
  accountMetadata: { [key: string]: AccountMetadatum };
  analysisDate?: Date;
  analytics: Analytics;
  annotations: Annotations;
  assignee?: string;
  comments: Analytics;
  constructionMethod: string;
  creationTime?: Date;
  creator?: string;
  currencyCode: string;
  expireTime?: Date;
  lastUpdater?: string;
  lastWorkflowStatus: LastWorkflowStatus;
  metadata: Metadata;
  origin: string;
  originalAggregateAnalytics: Analytics;
  originalNav?: number;
  passed: boolean;
  portfolioReference?: PortfolioReference;
  proposalId: string;
  proposedAggregateAnalytics: Analytics;
  proposedNav?: number;
  ruleAggregations: { [key: string]: string };
  rules: { [key: string]: string };
  scores: Analytics;
  snapshotId?: string;
  taxes: Analytics;
  underlyingAccountIds: string[];
  updateTime?: Date;
}

export interface AccountMetadatum {
  values: string[];
}

export interface Analytics {
  [key: string]: MetadataValue;
}

export interface Annotations {
  PURPOSE: string;
  SOURCE: string;
}

export interface LastWorkflowStatus {
  creationTime: Date;
  fixedComment?: string;
  proposalId: string;
  status: string;
  userComment?: string;
  userId: string;
  uuid: string;
}

export interface Metadata {
  name: Name;
  portfolioGroupId?: Name;
  proposedOnly?: Name;
}

export interface Name {
  stringValue: string;
}

export interface PortfolioReference {
  childReferences: PortfolioReference[];
  id: string;
  tag: string;
}

export interface ProposalRequest {
  id: string;
  pkg: string;
  resolveProposedAggregate: 'true' | 'false';
}

export interface AGraphOpenLot {
  costBasis: number;
  id: string;
  metadata: { [key: string]: MetadataValue };
  purchaseDate: string;
  quantity: number;
}

export interface AGraphClosedLot {
  assetId: string;
  closedDate?: string;
  costBasis?: number;
  gainLoss?: number;
  id: string;
  metadata: { [key: string]: MetadataValue };
  purchaseDate?: string;
  quantity?: number;
  saleValue?: number;
}

export interface AGraphBenchmarkComponent {
  assetId: string;
  id: string;
  notionalWeight?: number;
  posInfo: { [key: string]: MetadataValue };
  weight?: number;
}

export interface AGraphBenchmark {
  asOfTime?: string;
  components?: AGraphBenchmarkComponent[];
  creationTime?: string;
  currencyCode: string;
  id: string;
  lastUpdater?: string;
  metadata: { [key: string]: MetadataValue };
  name?: string;
  origin: string;
  updateTime?: string;
}

export interface AGraphBenchmarkEntity {
  asOfTime?: string;
  id: string;
  origin: string;
}
export interface AGraphFee {
  amount: number;
  capFee?: number;
  capfloorType: string;
  category: string;
  endYear?: number;
  eventType: string;
  floorFee?: number;
  maximumPositionMarketValue?: number;
  minimumPositionMarketValue: number;
  paymentMethod: string;
  percentMethod: string;
  startYear: number;
  subcategory: string;
}

export interface AGraphSleeveInfo {
  asOfTime?: string;
  benchmarkPortfolio?: AGraphBenchmark;
  benchmarkReference?: AGraphBenchmarkEntity;
  fees: AGraphFee[];
  id: string;
  metadata: { [key: string]: MetadataValue };
  tag: string;
}

export interface AGraphAccountSupplement {
  sleeveInfos?: AGraphSleeveInfo[];
}

export interface AGraphAccountPosition {
  assetId: string;
  id: string;
  marketValue?: number;
  notionalMarketValue?: number;
  notionalWeight?: number;
  openLots?: AGraphOpenLot[];
  posInfo: { [key: string]: MetadataValue };
  quantity?: number;
  weight?: number;
}

export interface AGraphModelComponent {
  assetId: string;
  id: string;
  notionalWeight?: number;
  posInfo: { [key: string]: MetadataValue };
  weight?: number;
}

export interface AGraphModel {
  asOfTime?: string;
  benchmarkPortfolio?: AGraphBenchmark;
  benchmarkReference?: AGraphBenchmarkEntity;
  components: AGraphModelComponent[];
  creationTime?: string;
  currencyCode: string;
  id: string;
  lastUpdater?: string;
  metadata: { [key: string]: MetadataValue };
  modelTag: string;
  name?: string;
  origin: string;
  otherBenchmarks: PortfolioManagementbenchmarksv1BenchmarkAssignment[];
  updateTime?: string;
}

export interface AGraphModelEntity {
  asOfTime?: string;
  id: string;
  modelTag: string;
  origin: string;
}

export interface AGraphModelAssignment {
  modelPortfolio: AGraphModel;
  modelReference: AGraphModelEntity;
  modelType: string;
}

export interface AGraphAccount {
  asOfTime?: string;
  benchmarkPortfolio?: AGraphBenchmark;
  benchmarkReference?: AGraphBenchmarkEntity;
  closedLots?: AGraphClosedLot[];
  creationTime?: string;
  currencyCode: string;
  id: string;
  lastUpdater?: string;
  metadata: { [key: string]: MetadataValue };
  modelPortfolio?: AGraphModel;
  modelPortfolios?: {
    models: AGraphModel;
  };
  modelReference?: AGraphModelEntity;
  modelReferences?: {
    modelAssignments: AGraphModelAssignment[];
  };
  name?: string;
  origin: string;
  otherBenchmarks?: PortfolioManagementbenchmarksv1BenchmarkAssignment[];
  otherModels: AGraphModelAssignment[];
  positions: AGraphAccountPosition[];
  supplement?: AGraphAccountSupplement;
  updateTime?: string;
}

export interface AGraphAccountValues {
  accounts: AGraphAccount[];
}

export interface AGraphAccountEntity {
  asOfTime?: string;
  id: string;
  origin: string;
}

export interface AGraphAggregate {
  accountEntities?: AGraphAccountEntities;
  accountValues?: AGraphAccountValues;
  asOfTime?: string;
  benchmarkPortfolio?: AGraphBenchmark;
  benchmarkReference?: AGraphBenchmarkEntity;
  childAggregateEntities?: {
    aggregateEntities: AGraphAggregateEntity[];
  };
  childAggregateValues?: {
    aggregates: AGraphAggregate[];
  };
  creationTime?: string;
  currencyCode: string;
  id: string;
  lastUpdater?: string;
  metadata?: { [key: string]: MetadataValue };
  modelPortfolio?: AGraphModel;
  modelPortfolios?: {
    models: AGraphModel;
  };
  modelReference?: AGraphModelEntity;
  modelReferences?: {
    modelAssignments: AGraphModelAssignment[];
  };
  name?: string;
  origin: string;
  otherBenchmarks?: PortfolioManagementbenchmarksv1BenchmarkAssignment[];
  otherModels?: AGraphModelAssignment[];
  tag: string;
  updateTime?: string;
}

export interface AGraphProposal {
  aggregate?: AGraphAggregate;
  annotations: { [key: string]: string };
  changeSet?: AGraphChangeSet;
  constructionMethod: string;
  creationTime?: Date;
  currencyCode: string;
  id: string;
  lastUpdater?: string;
  metadata?: { [key: string]: MetadataValue };
  name?: string;
  origin: string;
  portfolioReferences: PortfolioReference[];
  proposedAggregate?: AGraphAggregate;
  reason?: string;
  summary?: ProposalSummary;
  updateTime?: Date;
  workflowStatuses: LastWorkflowStatus[];
}

export interface AGraphAggregateTreeAgg {
  accountIds: string[];
  aggregateId: string;
  childAggregateTrees?: AGraphAggregateTree;
}

export interface AGraphAggregateTag {
  aggregates: AGraphAggregateTreeAgg[];
}

export interface AGraphAggregateTags {
  [aggIDTag: string]: AGraphAggregateTag;
}

export interface AGraphAggregateTree {
  aggregateTags: AGraphAggregateTags;
}

export interface AGraphRuleOverride {
  comment: string;
  ruleId?: string;
  ruleName?: string;
  ruleStatusOverridden?: boolean;
  source?: string;
  updateTime: DateAsString;
  userId: string;
}

export type ProposalRuleOverride = {
  comment: string;
  ruleId: string;
  ruleStatusOverridden?: boolean;
  updateTime: DateAsString;
  userId: string;
};

export type PortfolioRuleOverride = {
  comment: string;
  ruleName: string;
  ruleStatusOverridden?: boolean;
  source?: string;
  updateTime: DateAsString;
  userId: string;
  entityId: string;
  entityTag: string;
};
export interface AGraphProposalRuleOverride {
  proposalRuleOverrides: ProposalRuleOverride[];
}

export interface AGraphPortfolioRuleOverride {
  portfolioRuleOverrides: PortfolioRuleOverride[];
}

export interface AGraphResourceEntity {
  accountEntity?: AGraphAccountEntity;
  aggregateEntity?: AGraphAggregateEntity;
  benchmarkEntity?: AGraphBenchmarkEntity;
  modelEntity?: AGraphModelEntity;
}

export interface AGraphLinkedEntity {
  id: string;
  metadata?: { [key: string]: MetadataValue };
  resourceEntity?: AGraphResourceEntity;
  tag: string;
  updateTime?: DateAsString;
}
export interface AGraphLinkedEntitySelection {
  linkedEntity: AGraphLinkedEntity;
  selectionType: string;
  userSelectionInputs: { [key: string]: string };
}
export interface AGraphLinkedEntitySelections {
  selections: AGraphLinkedEntitySelection[];
}
export interface AGraphExternalProposalRuleEvaluations {
  externalProposalRuleEvaluations: ExternalProposalRuleEvaluation[];
}

export interface AGraphProposalSupplement {
  externalProposalRuleEvaluations?: AGraphExternalProposalRuleEvaluations;
  generationUserInput?: AGraphProposalGenerationUserInput;
  id: string;
  linkedEntitySelections?: AGraphLinkedEntitySelections;
  portfolioRuleOverrides?: AGraphPortfolioRuleOverride;
  proposalFees?: ProposalFees;
  proposalId: string;
  proposalRuleOverrides?: AGraphProposalRuleOverride;
  reportSettings?: ReportSettings;
  resolvedOrders?: V1ResolvedOrders;
  supervisorPortfolioRuleOverrides?: AGraphPortfolioRuleOverride;
  supervisorProposalRuleOverrides?: AGraphProposalRuleOverride;
  updateTime?: DateAsString;
}

export interface AGraphProposalSupplementsResponse {
  supplements: AGraphProposalSupplement[];
}
export interface AGraphMetadataChange {
  metadata?: { [key: string]: MetadataValue };
  portfolioTagId?: string;
}
export interface AGraphChangeSet {
  aggregateCurrency?: string;
  benchmarkChanges?: AGraphBenchmarkChange[];
  metadataChanges?: AGraphMetadataChange;
  positionChanges: AGraphPositionChange[];
}

export interface AGraphPositionChange {
  assetId: string;
  changeType: ChangeTypeMap;
  lotChanges: AGraphLotChange[];
  marketValue?: number;
  marketValueAggregateCurrency?: number;
  metadata: { [key: string]: MetadataValue };
  notionalMarketValue?: number;
  notionalMarketValueAggregateCurrency?: number;
  portfolioId: string;
  posInfoOverrides: { [key: string]: MetadataValue };
  positionId: string;
  quantity?: number;
  reason?: string;
}

export interface AGraphLotChange {
  lotId: string;
  marketValue?: number;
  metadata: { [key: string]: MetadataValue };
  notionalMarketValue?: number;
  quantity: number;
}

export interface AGraphBenchmarkChange {
  benchmarkPortfolio?: AGraphBenchmark;
  benchmarkReference?: AGraphBenchmarkEntity;
  portfolioTagId?: string;
}

export interface AGraphBatchGenerateProposal {
  accountEntities?: AGraphAccountEntities;
  aggregateEntities?: AGraphAggregateEntities;
  createTime?: string;
  creator?: string;
  generateProposalRequest?: AGraphBatchProposalGenerationUserInput;
  generateTasks?: AGraphGenerateProposalTask[];
  id?: string;
  metadata?: { [key: string]: MetadataValue };
  name?: string;
  state?: string;
  updateTime?: string;
}
export interface AGraphBatchGenerateProposalResponse {
  done?: boolean;
  error?: AGraphErrorResponse;
  id?: string;
  meta?: never;
  response?: AGraphBatchGenerateProposal;
}

export interface AGraphListBatchGenerateProposalByUserRequest {
  pageSize?: number;
  pageToken?: string;
  pkg: string;
}

export interface AGraphListBatchGenerateProposalByUserResponse {
  batchProposalGenerates: AGraphBatchGenerateProposal[];
  nextPageToken?: string;
}

export interface AgraphBatchGetBatchProposalsResponse {
  batchGenerates: AGraphBatchGenerateProposal[];
}

export interface CancelBatchProposalRequest {
  pkg: string;
}

export interface AGraphGenerateProposalTask {
  accountEntity?: AGraphAccountEntity;
  accountValues?: AGraphAccount;
  aggregateEntity?: AGraphAggregateEntity;
  batchId: string;
  id: string;
  metadata?: { [key: string]: MetadataValue };
  state?: string;
}

interface AGraphErrorResponse {
  code?: number;
  details?: never;
  message?: string;
}

interface AGraphBatchProposalGenerationUserInput {
  accountEntity?: AGraphAccountEntity;
  aggregateEntity?: AGraphAggregateEntity;
  asOfDate: string;
  configOverride?: object;
  generationUserInput?: AGraphProposalGenerationUserInput;
  pkg: string;
}

interface AGraphAccountEntities {
  accountEntities: AGraphAccountEntity[];
}
export interface AGraphAggregateEntity {
  asOfTime?: string;
  id: string;
  origin: string;
  tag: string;
}
interface AGraphAggregateEntities {
  aggregateEntities: AGraphAggregateEntity[];
}

export interface AGraphBatchProposalGenerationParameters {
  [key: string]: MetadataValue;
}

export interface GetMasterAssetAllocationRequestParams {
  breakdownName: string;
  currencyCode: string;
  overrideConfigDirectory?: string;
  pkg: string;
}
export interface MasterAssetSectors {
  assetId: string;
  children: MasterAssetSectors[];
  displayName: string;
  name: string;
  performanceAssetId: string;
}

export interface SectorsCollection {
  sectors: MasterAssetSectors[];
}

export interface GetMasterAssetAllocationResponse {
  [key: string]: SectorsCollection;
}
export interface IProposalAggregateSummariesRequestBody {
  pkg: string;
  query?: IQueryWithAggregationStatisticsParams;
}

export interface IFacetPivots {
  includeMissing: boolean;
  pivotFields: string[];
}

export interface IQueryWithAggregationStatisticsParams extends Query {
  aggregationStatisticParams?: ProposalsAggregationStatisticsParams[];
  facetPivots?: IFacetPivots;
}

export interface IStatisticResult {
  name: string;
  statisticValue: number | string;
}

export interface IFacetPivotResult {
  count: number;
  doubleValue?: number;
  fieldName: string;
  pivots: IFacetPivotResult[];
  stringValue?: string;
}
export interface IFacetPivotResults {
  results: IFacetPivotResult[];
}

export interface IProposalAggregateSummariesResponseBody {
  facetPivotResults: IFacetPivotResults;
  fieldCounts?: FieldCount[];
  statisticResults: IStatisticResult[];
}

export interface AgraphLinkedEntityOverlay {
  linkedEntityId: string;
  linkedEntityTag: string;
}

export interface AgraphGetAggregatesRequestBody {
  aggregates: AGraphAggregateEntity[];
  allowMissing?: boolean;
  benchmarkType?: string;
  composeAccounts?: boolean;
  disallowConflictAggregates?: boolean;
  ignoreEntitlement?: boolean;
  linkedEntityOverlay?: AgraphLinkedEntityOverlay;
  overrideConfigDirectory?: string;
  overrideConfigFile?: string;
  pkg: string;
  skipBenchmark?: boolean;
  viewAsUser?: string;
}

export interface AgraphGetAggregatesResponseBody {
  aggregates: AGraphAggregate[];
}

export interface BatchDeleteAggregatesRequestBody {
  aggregates: AGraphAggregateEntity[];
  pkg: string;
}

export interface ProposalWorkflowStatus {
  fixedComment?: string | null;
  status: string;
  userComment?: string | null;
}

export interface BatchUpdateWorkflowStatusesResponse extends ProposalWorkflowStatus {
  creationTime?: string;
  proposalId?: string;
  userId?: string;
  uuid?: string;
}

export interface BatchUpdateWorkflowStatusesRequestBody {
  assignee?: string;
  pkg: string;
  proposalIds: string[];
  proposalWorkflowStatus: BatchUpdateWorkflowStatusesResponse;
}

export interface IBatchUpdateWorkflowStatusesResponse {
  proposalWorkflowStatuses: BatchUpdateWorkflowStatusesResponse[];
}

export interface UpdateWorkflowStatusRequestBody extends BatchUpdateWorkflowStatusesResponse {
  assignee?: string;
  pkg: string;
}

export interface UpdateWorkflowStatusErrorResponse {
  code: string;
  error: string;
  message: string;
}

export interface BatchGetProposalsRequestBody {
  allowMissing?: boolean;
  ids: string[];
  includeSummary?: boolean;
  includeWorkflowHistory?: boolean;
  pkg: string;
  resolveProposedAggregate?: boolean;
}

export interface BatchGetProposalsResponseBody {
  proposals: AGraphProposal[];
}

export interface AgraphBatchGetModelsRequestBody {
  allowMissing?: boolean;
  ignoreEntitlement?: boolean;
  models: AGraphModelEntity[];
  pkg: string;
  viewAsUser?: string;
}

export interface AgraphBatchGetModelsResponseBody {
  models: AGraphModel[];
}

export type GetResolvedOrdersRequestBody = ResolvedOrderApiListResolvedOrdersData['query'];
export type RefreshOrderAttributesParams = ResolvedOrderApiRefreshOrderAttributesData['query'];
